/* eslint-disable import/no-duplicates */
import React, { useEffect, useMemo, useState } from 'react';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { useMediaQuery } from 'react-responsive';

import { ptBR } from 'date-fns/locale';
import {
  subDays,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  format,
} from 'date-fns';

import { rangeItems } from 'pages/TrackingRefuel/providers/rangeItems';

import CustomCalendar from './CustomCalendar';

import { useTrackingRefuel } from '../../../hooks/useTrackingRefuel';

import {
  Container,
  CalendarIconWrapper,
  CalendarText,
  CalendarWrapper,
  CalendarContainer,
  CalendarBody,
  OptionsWrapper,
  Option,
  OptionText,
  CalendarHeader,
  CalendarContent,
  CalendarFooter,
  CalendarButtonCancel,
  CalendarButtonConfirm,
  RangeData,
  RangeDataText,
  ButtonsFooterWrapper,
  MobileTitleWrapper,
  MobileTitle,
  CloseIcon,
} from './styles';

const Calendar: React.FC = () => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedOption, setSelectedOption] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { handleChangeStartDate, handleChangeEndDate } = useTrackingRefuel();

  const cancelFilterCalendar = (): void => {
    setSelectedOption(0);
    setStartDate(null);
    setEndDate(null);
    handleChangeStartDate(null);
    handleChangeEndDate(null);
    setShowCalendar(false);
  };

  const confirmFilterCalendar = (): void => {
    setShowCalendar(false);
    handleChangeStartDate(startDate);
    handleChangeEndDate(endDate);
  };

  const zeroHour = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  useEffect(() => {
    const today = zeroHour(new Date());

    switch (selectedOption) {
      case 1:
        setStartDate(today);
        setEndDate(today);
        break;
      case 2:
        setStartDate(subDays(today, 1));
        setEndDate(subDays(today, 1));
        break;
      case 3: {
        const startOfLastWeek = startOfWeek(subDays(today, 7), {
          weekStartsOn: 1,
        });
        const endOfLastWeek = today;
        setStartDate(zeroHour(startOfLastWeek));
        setEndDate(zeroHour(endOfLastWeek));
        break;
      }
      case 4:
        setStartDate(zeroHour(startOfMonth(today)));
        setEndDate(zeroHour(endOfMonth(today)));
        break;
      case 5:
        setStartDate(zeroHour(subDays(today, 30)));
        setEndDate(today);
        break;
      case 6:
        setStartDate(zeroHour(subDays(today, 60)));
        setEndDate(today);
        break;
      case 7:
        setStartDate(zeroHour(subDays(today, 90)));
        setEndDate(today);
        break;
      case 8:
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        setStartDate(null);
        setEndDate(null);
        break;
    }
  }, [selectedOption]);

  const rangeData = useMemo(() => {
    if (startDate && endDate) {
      return `${format(startDate, 'dd MMM, yyyy', { locale: ptBR })} - ${format(
        endDate,
        'dd MMM, yyyy',
        { locale: ptBR }
      )}`;
    }
    return '';
  }, [startDate, endDate]);

  return (
    <Container>
      <CalendarWrapper
        onClick={() => setShowCalendar((prevState) => !prevState)}
      >
        <CalendarIconWrapper>
          <MdOutlineCalendarToday size={24} color="#3d3d3d" />
        </CalendarIconWrapper>
        <CalendarText>{rangeData || 'Este mês'}</CalendarText>
      </CalendarWrapper>
      {showCalendar && (
        <CalendarContainer>
          <MobileTitleWrapper>
            <MobileTitle>Filtre por data</MobileTitle>
            <CloseIcon onClick={() => setShowCalendar(false)}>X</CloseIcon>
          </MobileTitleWrapper>
          <CalendarBody>
            <OptionsWrapper>
              {rangeItems.map((item) => (
                <Option
                  active={selectedOption === item.id}
                  key={item.id}
                  onClick={() => setSelectedOption(item.id)}
                >
                  <OptionText active={selectedOption === item.id}>
                    {item.name}
                  </OptionText>
                </Option>
              ))}
            </OptionsWrapper>
            <CalendarContent>
              <CalendarHeader>
                <CustomCalendar
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  customizing={selectedOption === 8}
                />
              </CalendarHeader>
              {isMobile && (
                <RangeData>
                  <RangeDataText>{rangeData}</RangeDataText>
                </RangeData>
              )}
            </CalendarContent>
          </CalendarBody>
          <CalendarFooter>
            {!isMobile && (
              <RangeData>
                <RangeDataText>{rangeData}</RangeDataText>
              </RangeData>
            )}
            <ButtonsFooterWrapper>
              <CalendarButtonCancel onClick={cancelFilterCalendar}>
                Cancelar
              </CalendarButtonCancel>
              <CalendarButtonConfirm onClick={confirmFilterCalendar}>
                Confirmar
              </CalendarButtonConfirm>
            </ButtonsFooterWrapper>
          </CalendarFooter>
        </CalendarContainer>
      )}
    </Container>
  );
};

export default Calendar;
